/**
 * Reports landing — config-driven report cards grouped by category.
 *
 * Implements `C:\workspace\lucid\reports-landing-wireframe.pdf`:
 *   A. Header with inline search top-right
 *   B. Category tabs (replaces oversized StatTile strip)
 *   C. Tile grid that fills the page width
 *
 * Mirrors the `/sales/customers` shape (header + tabs + toolbar + grid) but adapts
 * to the sparse reports surface — no KPI strip, no "more reports" placeholder.
 */

import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronRight, FileBarChart, Search as SearchIcon, Sparkles } from "lucide-react";
import { Alert, Button, Card, Skeleton, TabsList, TabsRoot, TabsTrigger } from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import { WorkspaceShell } from "../../../features/workspace/index.ts";
import { iconForReport } from "../../../features/reports/index.ts";

export const Route = createFileRoute("/(workspace)/reports/")({
  component: ReportsLandingPage,
});

const CATEGORY_TABS: Array<{ id: string; label: string }> = [
  { id: "all", label: "All" },
  { id: "general", label: "General" },
  { id: "sales", label: "Sales" },
  { id: "purchases", label: "Purchases" },
  { id: "cash", label: "Cash" },
  { id: "inventory", label: "Inventory" },
  { id: "payroll", label: "Payroll" },
];

const CATEGORY_LABELS: Record<string, string> = {
  sales: "Sales",
  purchases: "Purchases",
  cash: "Cash",
  inventory: "Inventory",
  payroll: "Payroll",
  general: "General",
};

interface ReportSummary {
  key: string;
  name: string;
  description: string | null;
  category: string;
}

function ReportsLandingPage() {
  const list = trpc.reports.listDefinitions.useQuery({});
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState<string>("all");

  const allRows: ReportSummary[] = list.data?.rows ?? [];

  // Counts per category, used for the tab chips.
  const counts = useMemo(() => {
    const c: Record<string, number> = { all: allRows.length };
    for (const r of allRows) c[r.category] = (c[r.category] ?? 0) + 1;
    return c;
  }, [allRows]);

  // Search-and-tab filter.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allRows.filter((r) => {
      if (tab !== "all" && r.category !== tab) return false;
      if (!q) return true;
      return (
        r.name.toLowerCase().includes(q) ||
        (r.description ?? "").toLowerCase().includes(q) ||
        r.category.toLowerCase().includes(q)
      );
    });
  }, [allRows, query, tab]);

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Reports", to: "/reports" }]}>
      <div className="space-y-4 p-6 max-w-7xl">
        {/* A — Header with inline search */}
        <header className="flex items-center justify-between gap-6 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight">Reports</h1>
            <p className="text-sm text-muted mt-1">
              Pre-built and config-driven reports for the workspace.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-full sm:w-[320px]">
              <label className="flex items-center gap-2 h-9 px-3 rounded-sm border border-line bg-bg focus-within:border-sky-600 focus-within:bg-paper focus-within:ring-2 focus-within:ring-sky-100 transition-colors">
                <SearchIcon size={14} strokeWidth={2} aria-hidden className="text-muted shrink-0" />
                <input
                  type="search"
                  placeholder="Search reports by name or description…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="flex-1 min-w-0 bg-transparent border-0 outline-0 text-sm placeholder:text-muted text-ink"
                  aria-label="Search reports"
                />
              </label>
            </div>

            <Button intent="primary" size="sm" asChild>
              <Link to="/reports/new">
                <Sparkles className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
                New AI report
              </Link>
            </Button>
          </div>
        </header>

        {/* B — Category tabs */}
        <TabsRoot value={tab} onValueChange={setTab}>
          <TabsList aria-label="Report categories">
            {CATEGORY_TABS.map((t) => (
              <TabsTrigger key={t.id} value={t.id} count={counts[t.id] ?? 0}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </TabsRoot>

        {/* C — Body */}
        {list.isLoading ? (
          <GridSkeleton />
        ) : list.error ? (
          <Alert intent="bad">Failed to load reports: {list.error.message}</Alert>
        ) : allRows.length === 0 ? (
          <EmptyState />
        ) : filtered.length === 0 ? (
          <NoMatchState query={query} tab={tab} onClear={() => { setQuery(""); setTab("all"); }} />
        ) : (
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((r) => (
              <ReportTile key={r.key} report={r} />
            ))}
          </div>
        )}
      </div>
    </WorkspaceShell>
  );
}

function ReportTile({ report }: { report: ReportSummary }) {
  const ReportIcon = iconForReport(report.key, report.category);
  return (
    <Link
      to="/reports/$reportKey"
      params={{ reportKey: report.key }}
      className="group flex flex-col gap-2.5 p-4 bg-paper border border-line rounded-sm hover:border-sky-600 hover:shadow-sm transition-all min-h-[160px]"
    >
      <span
        aria-hidden
        className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-sky-50 text-sky-700 shrink-0"
      >
        <ReportIcon size={16} strokeWidth={2} />
      </span>

      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-ink truncate">{report.name}</h3>
        {report.description ? (
          <p className="text-xs text-muted line-clamp-2 mt-1 leading-relaxed">
            {report.description}
          </p>
        ) : null}
      </div>

      <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-muted">
        <span>{CATEGORY_LABELS[report.category] ?? report.category}</span>
        <ChevronRight className="h-4 w-4 text-muted group-hover:text-sky-700 transition-colors" />
      </div>
    </Link>
  );
}

function EmptyState() {
  return (
    <Card className="p-12">
      <div className="flex flex-col items-center justify-center text-center max-w-md mx-auto">
        <div className="h-14 w-14 rounded-md bg-sky-50 text-sky-600 flex items-center justify-center mb-4">
          <FileBarChart size={28} strokeWidth={1.5} />
        </div>
        <h3 className="text-base font-semibold">No reports yet</h3>
        <p className="text-sm text-muted mt-2">
          Reports are seeded via{" "}
          <code className="px-1 py-0.5 bg-bg rounded text-xs">packages/db/src/seed/reports/*.sql</code>
          . Run{" "}
          <code className="px-1 py-0.5 bg-bg rounded text-xs">pnpm db:seed:company-reports</code>{" "}
          to add the demo set.
        </p>
      </div>
    </Card>
  );
}

function NoMatchState({
  query,
  tab,
  onClear,
}: {
  query: string;
  tab: string;
  onClear: () => void;
}) {
  const hasQuery = query.length > 0;
  const hasTab = tab !== "all";
  return (
    <Card className="p-10">
      <div className="flex flex-col items-center justify-center text-center">
        <SearchIcon size={32} strokeWidth={1.5} className="text-muted/40 mb-3" />
        <h3 className="text-sm font-medium">
          {hasQuery && hasTab
            ? `No ${tab} reports match "${query}"`
            : hasQuery
              ? `No reports match "${query}"`
              : `No ${tab} reports yet`}
        </h3>
        <button
          onClick={onClear}
          className="text-xs text-sky-700 hover:underline mt-2 cursor-pointer"
        >
          Clear filters
        </button>
      </div>
    </Card>
  );
}

function GridSkeleton() {
  return (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div
          key={i}
          className="flex flex-col gap-2.5 p-4 bg-paper border border-line rounded-sm min-h-[160px]"
        >
          <Skeleton className="h-8 w-8 rounded-md" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-5/6" />
          <div className="flex-1" />
          <div className="flex items-center justify-between">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-4 w-4" />
          </div>
        </div>
      ))}
    </div>
  );
}
